// ----------------------------------------------------------------------
// <copyright file="Izenda.DatabaseMappingStore.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

/**
 * Database Mapping Store
 */
!(function() {
  var m = require('mobx');
  var uiState = require('logic/Izenda.UiState.js');
  var fetcher = require('fetchers/Izenda.DatabaseMappingFetcher');
  var tenantFetcher = require('fetchers/Izenda.TenantManagementDataFetcher');

  var MappingTenant = require('logic/models/Izenda.MappingTenant');
  var DatabaseMappingDetail = require('logic/models/Izenda.DatabaseMappingDetail');
  var ResponseMessage = require('logic/models/Izenda.ResponseMessage');
  var constants = require('logic/data/Izenda.DatabaseMappingConstants');
  var LazySelectDataSource = require('logic/models/Izenda.LazySelectDataSource');
  var MAPPING_TYPE = constants.MAPPING_TYPE;
  var localizer = require('share/localization/Izenda.Localizer');
  const { forEach, map, isNil, concat, sortBy, first, getOr, find } = require('lodash/fp');

  function DatabaseMappingStore() {
    this.typeOptions = constants.MAPPING_TYPES;
    m.extendObservable(this, {
      originalTenants: [], // Collection of MappingTenant
      fromDatabaseNameDataSource: new LazySelectDataSource(
        constants.DATABASE_NAME_OPTION_MAPPINGS,
        constants.DATABASE_NAME_KEY
      ),
      toDatabaseNameDataSource: new LazySelectDataSource(
        constants.DATABASE_NAME_OPTION_MAPPINGS,
        constants.DATABASE_NAME_KEY
      ),
      objectDataSources: [], // { id: constants.DATABASE_NAME_KEY, value: LazySelectDataSource }
      databaseMappingDetail: new DatabaseMappingDetail(),
      responseMessage: new ResponseMessage(), // Object of ResponseMessage

      mappingTenants: function() {
        // data source for mapping drop down.
        return concat([constants.ALL_MAPPINGS_OPTION], sortBy(['tenantId'], this.originalTenants));
      },
      tenantOptions: function() {
        // data source for mapping select component.
        return concat([constants.ALL_TENANT_OPTION], sortBy(['tenantId'], this.originalTenants));
      }
    });
  }

  /**
   * Initial database mapping.
   * Static collections, load mapping detail
   */
  DatabaseMappingStore.prototype.init = m.action(function init() {
    uiState.setCheckDirtyFunction(
      function() {
        return this.databaseMappingDetail.checkDirty();
      }.bind(this)
    );

    return Promise.join(
      tenantFetcher.getAllNameTenantList(),
      this.loadDatabaseNames(true),
      this.loadDatabaseNames(false),
      this.loadDataMapping(),
      function(tenantList) {
        this.originalTenants = map(function(item) {
          return new MappingTenant(item);
        }, tenantList);

        if (isNil(this.databaseMappingDetail.selectedTenantId)) {
          this.databaseMappingDetail.setPropertyValue('selectedTenantId', constants.ALL_TENANT_ID, true);
        }
      }.bind(this)
    );
  });

  /**
   * Unlink between store and uiState
   */
  DatabaseMappingStore.prototype.destroy = function() {
    uiState.resetCheckDirtyFunction();
    this.databaseMappingDetail.destroy();
  };

  /**
   * Load database names (lazy loading)
   * @param {Boolean} isFrom
   * @param {Object} pageInfo
   */
  DatabaseMappingStore.prototype.loadDatabaseNames = function(isFrom, pageInfo) {
    var dataSource = isFrom ? this.fromDatabaseNameDataSource : this.toDatabaseNameDataSource;
    var request = {
      skipItems: pageInfo ? pageInfo.fromIndex : dataSource.skipItems,
      pageSize: pageInfo ? pageInfo.pageSize : dataSource.pageSize,
      loadFromDatabase: isFrom
    };

    return fetcher
      .loadDatabaseNames(request)
      .bind(this)
      .then(function(response) {
        var detail = {
          skipItems: response.skipItems,
          pageSize: response.pageSize,
          isLastPage: response.isLastPage,
          items: response.result
        };
        return dataSource.setDetail(detail);
      });
  };

  /**
   * Load objects (lazy loading)
   * @param {Object} mappingItem
   * @param {Object} pageInfo
   */
  DatabaseMappingStore.prototype.loadObjects = m.action(function loadObjects(mappingItem, pageInfo) {
    var databaseServer = mappingItem.fromServer;
    var fromDatabaseName = mappingItem.fromDatabaseName;
    var fromDatabaseUser = mappingItem.fromDatabaseUser;
    var optionFromDatabaseNameValue = mappingItem.optionFromDatabaseNameValue;
    var type = mappingItem.type;
    var request, objects;

    var matchingDataSource = find(function(item) {
      return item.id === optionFromDatabaseNameValue;
    }, this.objectDataSources);

    if (isNil(matchingDataSource)) {
      matchingDataSource = m.observable({
        id: optionFromDatabaseNameValue,
        schemaObjects: new LazySelectDataSource(constants.OBJECT_OPTION_MAPPINGS, constants.OBJECT_KEY),
        databaseObject: new LazySelectDataSource(constants.OBJECT_OPTION_MAPPINGS, constants.OBJECT_KEY)
      });

      this.objectDataSources.push(matchingDataSource);
    }

    objects = type === MAPPING_TYPE.Schema ? matchingDataSource.schemaObjects : matchingDataSource.databaseObject;

    request = {
      skipItems: pageInfo ? pageInfo.fromIndex : objects.skipItems,
      pageSize: pageInfo ? pageInfo.pageSize : objects.pageSize,
      databaseServer: databaseServer,
      databaseName: fromDatabaseName,
      databaseUser: fromDatabaseUser,
      type: type
    };

    var setFirstOption = function() {
      var value;
      if (MAPPING_TYPE.Database === type) {
        var firstDatabase = first(objects.items);
        value = firstDatabase && firstDatabase[constants.OBJECT_KEY];
        mappingItem.setPropertyValue('fromObject', value);
      }
      mappingItem.setPropertyValue('fromObject', value);
    };

    return fetcher.loadObjects(request).then(function(response) {
      var detail = {
        skipItems: response.skipItems,
        pageSize: response.pageSize,
        isLastPage: response.isLastPage,
        items: response.result
      };
      objects.setDetail(detail);
      setFirstOption();
    });
  });

  /**
   * Get object data source.
   * @param {String} databaseName
   */
  DatabaseMappingStore.prototype.getObjectDataSource = function(databaseName) {
    return find(function(item) {
      return item.id === databaseName;
    }, this.objectDataSources);
  };

  /**
   * Add a new mapping.
   */
  DatabaseMappingStore.prototype.addMapping = function() {
    var firstDatabase = first(this.fromDatabaseNameDataSource.items);
    var databaseName = firstDatabase && firstDatabase.labelField;
    var databaseUser = firstDatabase && firstDatabase.databaseUser;
    var server = firstDatabase && firstDatabase.databaseServer;

    this.databaseMappingDetail.addMapping(server, databaseName, databaseUser);
  };

  /**
   * Set alert detail.
   * @param {Object} type
   * @param {Object} message
   */
  DatabaseMappingStore.prototype.setAlertDetail = function(type, message) {
    this.responseMessage = new ResponseMessage(type, message);
  };

  /**
   * Load database mapping.
   */
  DatabaseMappingStore.prototype.loadDataMapping = function() {
    return fetcher
      .loadDataMapping()
      .bind(this)
      .then(function(response) {
        var success = getOr(true, 'success', response);
        if (success) {
          this.databaseMappingDetail.populate(response);
        }
      });
  };

  /**
   * Save database mapping.
   */
  DatabaseMappingStore.prototype.saveDataMapping = m.action(function() {
    this.databaseMappingDetail.mergeMappings();
    var request = this.databaseMappingDetail.buildDataForSaving();
    return fetcher
      .saveDataMapping(request)
      .bind(this)
      .then(function(response) {
        if (response.success) {
          this.setAlertDetail('success', localizer.getText('COMMON_MESSAGES_DETAILS_WERE_SAVED'));
          this.databaseMappingDetail.resetDirty();
          return this.loadDataMapping();
        } else {
          forEach(
            function(message) {
              var index = message.key;
              var info = message.messages[0];
              this.databaseMappingDetail.setMappingError(index, info);
            }.bind(this),
            response.messages
          );
        }
      });
  });

  module.exports = DatabaseMappingStore;
})();



// WEBPACK FOOTER //
// ./ui/logic/Izenda.DatabaseMappingStore.js